/**
 * Interactive Type Validators — ordering, matching, dragDropFill
 *
 * Split from typeStage.ts (2786 LOC god-file) — P2-S2
 */

import type { ValidationIssue } from './shared'
import { STAGE, blocker, coach } from './shared'

// ─── Ordering Validation ──────────────────────────────────────────────────────

export function validateOrdering(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const items = data.items
  const correctOrder = data.correctOrder

  if (
    items &&
    Array.isArray(items) &&
    correctOrder &&
    Array.isArray(correctOrder)
  ) {
    const itemsArr = items as unknown[]
    const orderArr = correctOrder as number[]

    if (orderArr.length !== itemsArr.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ORDERING_LENGTH_MISMATCH',
          `correctOrder length (${orderArr.length}) must match items length (${itemsArr.length})`,
          {
            field: 'data.correctOrder',
            expected: String(itemsArr.length),
            actual: String(orderArr.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    const sorted = [...orderArr].sort((a, b) => a - b)
    const expected = Array.from({ length: itemsArr.length }, (_, i) => i)
    if (JSON.stringify(sorted) !== JSON.stringify(expected)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ORDERING_INVALID_PERMUTATION',
          'correctOrder must be a permutation of [0, 1, 2, ...]',
          {
            field: 'data.correctOrder',
            expected: `[${expected.join(', ')}]`,
            actual: `[${orderArr.join(', ')}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < itemsArr.length; i++) {
      if (
        typeof itemsArr[i] !== 'string' ||
        (itemsArr[i] as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_ORDERING_EMPTY_ITEM',
            `items[${i}] must be a non-empty string`,
            {
              field: `data.items[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Matching Validation ──────────────────────────────────────────────────────

export function validateMatching(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const rawPairs =
    (data.pairs as unknown[] | undefined) ??
    ((data.matching as Record<string, unknown> | undefined)?.pairs as
      | unknown[]
      | undefined)

  if (rawPairs && Array.isArray(rawPairs)) {
    if (rawPairs.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_MATCHING_TOO_FEW',
          'Matching should have at least 2 pairs',
          {
            field: 'data.pairs',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }

    for (let i = 0; i < rawPairs.length; i++) {
      const pair = rawPairs[i] as Record<string, unknown> | undefined
      if (!pair || typeof pair !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MATCHING_INVALID_PAIR',
            `pairs[${i}] must be an object with left and right`,
            {
              field: `data.pairs[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof pair.left !== 'string' ||
          (pair.left as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_EMPTY_LEFT',
              `pairs[${i}].left must be a non-empty string`,
              {
                field: `data.pairs[${i}].left`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (
          typeof pair.right !== 'string' ||
          (pair.right as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_EMPTY_RIGHT',
              `pairs[${i}].right must be a non-empty string`,
              {
                field: `data.pairs[${i}].right`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }

    return issues
  }

  // Canonical format: leftItems + rightItems + correctMatches
  const left = data.leftItems as unknown[] | undefined
  const right = data.rightItems as unknown[] | undefined
  const matches = data.correctMatches as Record<string, number> | undefined

  if (!left && !right && !rawPairs) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MATCHING_NO_DATA',
        'Matching type requires either data.pairs or data.leftItems + data.rightItems + data.correctMatches',
        {
          field: 'data',
          questionId: qId,
          familyId: fId,
          suggestion:
            'Add data.pairs = [{left: "...", right: "..."}, ...] or data.leftItems + data.rightItems + data.correctMatches',
        },
      ),
    )
    return issues
  }

  if (left && Array.isArray(left) && right && Array.isArray(right)) {
    if (left.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_MATCHING_TOO_FEW',
          'Matching should have at least 2 pairs',
          {
            field: 'data.leftItems',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }

    if (matches && typeof matches === 'object') {
      for (const [leftIdx, rightIdx] of Object.entries(matches)) {
        const li = Number(leftIdx)
        const ri = rightIdx
        if (li < 0 || li >= left.length) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_INVALID_LEFT_IDX',
              `correctMatches key ${leftIdx} out of range [0, ${left.length - 1}]`,
              {
                field: `data.correctMatches.${leftIdx}`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (typeof ri !== 'number' || ri < 0 || ri >= right.length) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_INVALID_RIGHT_IDX',
              `correctMatches value for key ${leftIdx} out of range [0, ${right.length - 1}]`,
              {
                field: `data.correctMatches.${leftIdx}`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Drag Drop Fill Validation ────────────────────────────────────────────────

export function validateDragDropFill(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const template = data.template
  const draggables = data.draggables as unknown[] | undefined
  const placements = data.correctPlacements as
    | Record<string, number>
    | undefined

  if (typeof template !== 'string' || (template as string).trim() === '') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_DRAG_DROP_NO_TEMPLATE',
        'drag_drop_fill requires non-empty data.template',
        {
          field: 'data.template',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  } else {
    const placeholderMatches = (template as string).match(/\{(\d+)\}/g)
    if (!placeholderMatches || placeholderMatches.length === 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_DRAG_DROP_NO_PLACEHOLDERS',
          'Template must contain {0}, {1}, etc. placeholders',
          {
            field: 'data.template',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (draggables && Array.isArray(draggables)) {
    for (let i = 0; i < draggables.length; i++) {
      if (
        typeof draggables[i] !== 'string' ||
        (draggables[i] as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_DRAG_DROP_EMPTY_DRAGGABLE',
            `draggables[${i}] must be a non-empty string`,
            {
              field: `data.draggables[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  if (
    placements &&
    typeof placements === 'object' &&
    draggables &&
    Array.isArray(draggables)
  ) {
    for (const [placeholderIdx, draggableIdx] of Object.entries(placements)) {
      if (
        typeof draggableIdx !== 'number' ||
        draggableIdx < 0 ||
        draggableIdx >= draggables.length
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_DRAG_DROP_INVALID_PLACEMENT',
            `correctPlacements[${placeholderIdx}] value ${draggableIdx} out of draggables range [0, ${draggables.length - 1}]`,
            {
              field: `data.correctPlacements.${placeholderIdx}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  // ─── with_bank mode: require at least one decoy ─────────────────────────
  // When the seed declares data.mode === 'with_bank' the UI shows a
  // styled "woordbank" panel. Pedagogically that only adds value when the
  // bank contains decoys — otherwise the learner can solve the cloze by
  // pure elimination. Emit a coach issue when no decoy is present.
  if (data.mode === 'with_bank') {
    if (
      draggables &&
      Array.isArray(draggables) &&
      placements &&
      typeof placements === 'object'
    ) {
      const usedIndices = new Set<number>()
      for (const v of Object.values(placements)) {
        if (typeof v === 'number') usedIndices.add(v)
      }
      const decoyCount = draggables.length - usedIndices.size
      if (decoyCount <= 0) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_DRAG_DROP_BANK_NO_DECOY',
            'with_bank drag_drop_fill should include at least one decoy in draggables (otherwise the bank is solvable by elimination)',
            {
              field: 'data.draggables',
              questionId: qId,
              familyId: fId,
              severity: 45,
              suggestion:
                'Add at least one extra draggable that is not referenced by correctPlacements.',
            },
          ),
        )
      }
    }
  }

  return issues
}
